import { useState } from 'react';
import { Check, X, MessageSquare, Clock } from 'lucide-react';
import {
  usePendingLeaves,
  useLeaveRequests,
  useApproveLeave,
  useRejectLeave,
} from '@/lib/queries';
import { initials } from '@isoft-people/utils';

type Tab = 'pending' | 'approved' | 'rejected' | 'all';

const TABS: { key: Tab; label: string }[] = [
  { key: 'pending', label: 'Pendientes' },
  { key: 'approved', label: 'Aprobadas' },
  { key: 'rejected', label: 'Rechazadas' },
  { key: 'all', label: 'Todas' },
];

const AVATAR_COLORS = [
  'from-primary to-primary-light',
  'from-accent to-green-400',
  'from-warning to-yellow-400',
  'from-error to-pink-400',
  'from-info to-cyan-400',
  'from-purple-500 to-indigo-400',
];

const TYPE_LABELS: Record<string, string> = {
  VACATION: 'Vacaciones',
  MEDICAL: 'Permiso médico',
  PERSONAL: 'Permiso personal',
  BEREAVEMENT: 'Duelo',
  MATERNITY: 'Maternidad',
  PATERNITY: 'Paternidad',
  STUDY: 'Estudios',
  OTHER: 'Otro',
};

export function ManagerInboxPage() {
  const [tab, setTab] = useState<Tab>('pending');
  const { data: pending } = usePendingLeaves();
  const { data: all } = useLeaveRequests({});
  const approve = useApproveLeave();
  const reject = useRejectLeave();

  const items =
    tab === 'pending'
      ? pending?.items
      : tab === 'approved'
      ? all?.items?.filter((l: any) => l.status === 'APPROVED')
      : tab === 'rejected'
      ? all?.items?.filter((l: any) => l.status === 'REJECTED')
      : all?.items;

  const pendingCount = pending?.items?.length ?? 0;

  const handleReject = async (id: string) => {
    const reason = prompt('Motivo del rechazo:');
    if (!reason) return;
    await reject.mutateAsync({ id, reason });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-font-primary">Bandeja de Aprobaciones</h1>
          <p className="mt-1 text-sm text-font-secondary">
            Revisa y gestiona las solicitudes de tu equipo
          </p>
        </div>
        {pendingCount > 0 && (
          <span className="flex h-8 items-center rounded-full bg-warning/10 px-3 text-xs font-semibold text-warning">
            {pendingCount} pendientes
          </span>
        )}
      </div>

      <div className="rounded-lg border border-border bg-white shadow-sm">
        <div className="flex gap-1 border-b border-border px-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`relative px-4 py-4 text-sm font-semibold transition ${
                tab === t.key ? 'text-primary' : 'text-font-secondary hover:text-font-primary'
              }`}
            >
              {t.label}
              {tab === t.key && (
                <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t bg-primary" />
              )}
            </button>
          ))}
        </div>

        <div className="divide-y divide-border-light">
          {items?.map((lr: any, i: number) => {
            const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
            const name = `${lr.employee.firstName} ${lr.employee.lastName}`;
            const fmt = (d: string) =>
              new Date(d).toLocaleDateString('es', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              });
            return (
              <div key={lr.id} className="flex items-center gap-4 p-5 hover:bg-bg">
                <div
                  className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${color} text-sm font-semibold text-white`}
                >
                  {initials(name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-font-primary">{name}</p>
                    <span className="rounded-full bg-bg px-2 py-0.5 text-[11px] text-font-secondary">
                      {TYPE_LABELS[lr.type] ?? lr.type}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-font-secondary">
                    {fmt(lr.startDate)} → {fmt(lr.endDate)} ·{' '}
                    <span className="font-semibold">{lr.workingDays} días</span>
                  </p>
                  <p className="mt-1 text-xs text-font-tertiary">
                    <span className="font-semibold">Motivo:</span>{' '}
                    {lr.reason ?? 'Sin descripción'}
                  </p>
                </div>

                {tab === 'pending' && (
                  <div className="flex items-center gap-1">
                    <div className="mr-2 flex items-center gap-1 rounded-full bg-yellow-50 px-2 py-1 text-[10px] font-semibold text-warning">
                      <Clock size={10} />
                      Hace 2 días
                    </div>
                    <button
                      onClick={() => approve.mutate(lr.id)}
                      className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-light text-accent hover:bg-accent hover:text-white"
                      title="Aprobar"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={() => handleReject(lr.id)}
                      className="flex h-9 w-9 items-center justify-center rounded-md bg-red-50 text-error hover:bg-error hover:text-white"
                      title="Rechazar"
                    >
                      <X size={16} />
                    </button>
                    <button
                      className="flex h-9 w-9 items-center justify-center rounded-md bg-bg text-font-secondary hover:bg-border"
                      title="Comentar"
                    >
                      <MessageSquare size={16} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {(!items || items.length === 0) && (
            <p className="py-12 text-center text-sm text-font-secondary">
              Sin solicitudes en esta categoría
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
